import type { MsaasJobListing } from '~/types/hlx/msaasJobListing';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { getData, postData } from '~/utils/service';
import { sendInitialInquiry } from '../initialInquiry';
import { beginNegotiation } from '../negotiations/beginNegotiation';
import { MARKET_READ_TIMEOUT_MS } from '../requestPolicy';
import { AssociatedProviderOption, OnConfirmMessageEvent, Scope } from './types';

const MSAAS_ROUTE_NAME = 'msaasListingSearch';

// [MCV-1889] The Buying tab searches OFFERING listings. Its filters differ from the Selling tab's
// (a buyer location and required license states instead of work locations, FTEs and contract value).
const offeringParams = (input: Scope['context']) => {
  const filters = input.filters;
  return {
    listingSide: 'OFFERING',
    providerCategoryId: filters?.providerCategory?.value?.toString(),
    specialtyId: filters?.aoiSpecialty?.value?.toString(),
    subspecialtyIds: filters?.subspecialties?.map((sub) => sub.value).join(',') || undefined,
    requiredStateCodes: filters?.states?.map((state) => state.value).join(',') || undefined,
    nearLat: filters?.location?.latitude?.toString(),
    nearLng: filters?.location?.longitude?.toString(),
    compareListingId: filters?.compareListing?.value?.toString(),
    page: input.pagination.page.toString(),
    pageSize: input.pagination.pageSize.toString(),
    sortBy: input.sortBy,
  };
};

export type CompareListingOption = MsaasJobListing & { listingSide?: string };

export const API_CALLS = {
  get: async ({ input, signal }: { input: Scope; signal?: AbortSignal }) => {
    const filters = input.context.filters;
    const sortBy = input.context.sortBy;
    if (input.context.listingSide === 'OFFERING') {
      return await withRequestDeadline(
        (requestSignal) =>
          getData(MSAAS_ROUTE_NAME, {
            signal: requestSignal,
            params: offeringParams(input.context),
          }),
        MARKET_READ_TIMEOUT_MS,
        signal
      );
    }
    return await withRequestDeadline(
      (requestSignal) =>
        getData(MSAAS_ROUTE_NAME, {
          signal: requestSignal,
          params: {
            providerCategoryId: filters?.providerCategory?.value?.toString(),
            specialtyId: filters?.aoiSpecialty?.value?.toString(),
            subspecialtyIds:
              filters?.subspecialties?.map((sub) => sub.value).join(',') || undefined,
            stateCodes: filters?.states?.map((state) => state.value).join(','),
            workLocations: filters?.workLocation?.map((location) => location.value).join(','),
            fteMin: filters?.minFte?.toString(),
            fteMax: filters?.maxFte?.toString(),
            contractValueMin: filters?.minContractValue?.toString(),
            contractValueMax: filters?.maxContractValue?.toString(),
            startDateAfter: filters?.startDate,
            page: input.context.pagination.page.toString(),
            pageSize: input.context.pagination.pageSize.toString(),
            sortBy: sortBy,
          },
        }),
      MARKET_READ_TIMEOUT_MS,
      signal
    );
  },
  // The buyer's own listings that a fit can be compared against.
  getCompareListings: async (employerId: number): Promise<CompareListingOption[]> =>
    await getData<CompareListingOption[]>('msaasListing', {
      pathParams: { employerId: employerId.toString() },
    }),
  // 202 `{ status }`: PENDING when scoring was queued, otherwise why nothing was (COMPLETED, NOT_SCORED,
  // BELOW_MIN_PROVIDERS, NO_ELIGIBLE_PROVIDERS). Either way the result arrives through the next search read.
  checkFit: async ({
    offeringListingId,
    seekingListingId,
  }: {
    offeringListingId: number;
    seekingListingId: number;
  }): Promise<{ status?: string } | undefined> =>
    await postData('msaasListingFitCheck', {
      pathParams: { offeringId: offeringListingId.toString() },
      data: { seekingListingId },
    }),
  getPickerProviders: async ({
    employerId,
    search,
  }: {
    employerId: number;
    search?: string;
  }): Promise<AssociatedProviderOption[]> => {
    return await getData('hlxPickerProviders', {
      pathParams: { employerId: employerId.toString() },
      params: { search: search ?? '', limit: '50' },
    });
  },
};

const getProviderIds = (event: Scope['event']): number[] => {
  if (event.type === 'ON_CONFIRM_APPLY' || event.type === 'ON_CONFIRM_MESSAGE') {
    return [...(event.payload?.providerIds ?? [])];
  }
  return [];
};

export const JOB_RESPONSE_APIS = {
  apply: async ({ input, signal }: { input: Scope; signal?: AbortSignal }) => {
    const attempt =
      input.event.type === 'ON_RETRY_ACTION' ? input.context.actionAttempt : undefined;
    const listingId = attempt?.listingId ?? input.context.listing?.id;
    if (listingId == null) {
      throw new Error('Select a listing before applying.');
    }
    const result = await beginNegotiation(
      {
        listingType: 'MSAAS',
        msaasListingId: listingId,
        providerIds: attempt?.providerIds ?? getProviderIds(input.event),
      },
      signal
    );
    if (!result?.jobResponse) {
      throw new Error('Unable to complete response. Please try again.');
    }
    return result.jobResponse;
  },
  message: async ({ input, signal }: { input: Scope; signal?: AbortSignal }) => {
    const attempt =
      input.event.type === 'ON_RETRY_ACTION' ? input.context.actionAttempt : undefined;
    const listingId = attempt?.listingId ?? input.context.listing?.id;
    if (listingId == null) {
      throw new Error('Select a listing before messaging.');
    }
    const message = attempt?.message ?? (input.event as OnConfirmMessageEvent).payload.message;
    return await sendInitialInquiry(
      {
        listingType: 'MSAAS',
        msaasListingId: listingId,
        providerIds: attempt?.providerIds ?? getProviderIds(input.event),
        messageBody: message,
      },
      signal
    );
  },
};
